import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { ArrowRight } from '@/components/icons/ArrowRight'
import { useSurveyCategories } from '@/hooks/useSurveyCategories'
import { isRTL } from '@/locale/utils'
import { cn } from '@/utils/tailwind'

interface IndicationsGridProps {
  onChange: (id: number) => void
}

export const IndicationsGrid = ({ onChange }: IndicationsGridProps) => {
  const { t } = useTranslation()
  const { surveyCategories: indicationCategories } = useSurveyCategories()
  const containerRef = useRef<HTMLDivElement>(null)

  const [openedCategory, setOpenedCategory] = useState<string | null>(null)

  return (
    <div className="flex flex-col" ref={containerRef} data-testid="indications-grid">
      {indicationCategories!.map((category) => {
        const isOpened = openedCategory === category.id
        const numRows = Math.ceil(category.options.length / 3)
        const containerWidth = containerRef.current?.offsetWidth ?? 0

        return (
          <div key={category.id} className="border-b-gray-text/25 border-b" data-testid={`category-${category.id}`}>
            <button
              className="flex w-full items-center justify-between px-4 py-6"
              onClick={() => setOpenedCategory((prev) => (prev === category.id ? null : category.id))}
            >
              <div className="flex items-center gap-4">
                <img src={category.iconUrl} className="aspect-square h-6" />
                <h4 className="font-bold uppercase">{t(category.textKey)}</h4>
              </div>

              <ArrowRight
                className={cn('h-4 text-black transition-[color,transform]', isOpened && 'text-primary rotate-90', isRTL() && 'rotate-180 transform')}
              />
            </button>

            <div
              className="overflow-hidden px-1 transition-[max-height] duration-500"
              style={{
                maxHeight: isOpened ? (containerWidth / 3) * numRows + numRows * 75 : 0
              }}
            >
              <div className={`animate-fade-in mb-4 grid grid-cols-3 gap-4 pb-1 ${isOpened ? 'animate-fade-in' : 'animate-fade-out'}`}>
                {category.options
                  .sort((a, z) => a.order - z.order)
                  .map((indication) => (
                    <button
                      key={`${category.id}-${indication.id}`}
                      className="flex flex-col items-center justify-center gap-4 rounded-xl bg-white p-4 shadow-sm"
                      onClick={() => onChange(indication.id)}
                      data-testid={`indication-${indication.id}`}
                    >
                      <img src={indication.iconUrl} className="h-9" />

                      <span className="text-center text-sm">{t(indication.textKey)}</span>
                    </button>
                  ))}
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}
